/**
 * Список звітів про внесок на підтримку працевлаштування осіб з інвалідністю
 * (ч.1208, J0500701 / F0500701) — за зразком декларацій єдинника:
 * InfiniteDocGrid + статус подання з Журналу Кабінету ДПС.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  QUARTER_LABELS, REPORT_TYPE_LABELS, disabilityContributionReports,
  type DisabilityContributionReport,
} from '@/api/disabilityContributionReports';
import { dpsSubmissions } from '@/api/dpsSubmissions';
import { orgs as orgsApi } from '@/api/catalogs';
import { DocListFiltersRow } from '@/shared/components/DocListFilters';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { fmtMoney } from './SingleTaxBlank';

const SUB_STATUS: Record<string, [string, string]> = {
  DRAFT: ['чернетка', 'var(--muted)'],
  SIGNED: ['підписаний', 'var(--badge-blue-fg)'],
  SENT: ['відправлений', 'var(--badge-blue-fg)'],
  DELIVERED: ['обробляється', 'var(--badge-blue-fg)'],
  ACCEPTED: ['прийнятий', 'var(--badge-green-fg)'],
  REJECTED: ['не прийнятий', 'var(--badge-red-fg)'],
  ERROR: ['помилка', 'var(--badge-red-fg)'],
};

/** Короткий підсумок звіту для списку. */
export function reportOutcome(d: DisabilityContributionReport): string {
  const f = d.fields ?? {};
  if (f.is_payer) return `внесок ${fmtMoney(d.values?.R023G3 ?? '0')}`;
  if (f.below_threshold) return 'менше 8 працівників';
  if (f.norm_met) return 'норматив виконано';
  return 'без внеску';
}

export default function DisabilityContributionReportsPage() {
  const navigate = useNavigate();
  const { hasPermissionInAnyOrg, totalAccessibleOrgs } = usePermissionsByOrg('payroll.manage');
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgsApi.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgListQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgListQ.data]);
  const [orgId, setOrgId] = useOrgFilter();
  const period = useDefaultPeriod();
  const [fYear, setFYear] = useState<string>(() => period.dateFrom ? period.dateFrom.slice(0, 4) : '');
  const [fQuarter, setFQuarter] = useState<string>('');

  const { datasource, total } = useInfiniteGridDatasource<DisabilityContributionReport>(
    (limit, offset, sort) => disabilityContributionReports.list({
      organization_id: orgId || undefined,
      period_year: fYear ? Number(fYear) : undefined,
      period_quarter: fQuarter ? Number(fQuarter) : undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId, fYear, fQuarter],
  );

  const subsQ = useQuery({
    queryKey: ['disability-report-subs'],
    queryFn: () => dpsSubmissions.list({ source_type: 'doc.disability_contribution_reports', limit: 500 }),
  });
  const subByDoc = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of subsQ.data?.items ?? []) {
      if (s.source_id && !m.has(s.source_id)) m.set(s.source_id, s.status);
    }
    return m;
  }, [subsQ.data]);

  const cols = useMemo<ColDef<DisabilityContributionReport>[]>(() => [
    { colId: 'period', headerName: 'Період', width: 150,
      valueGetter: (p) => p.data ? `${QUARTER_LABELS[p.data.period_quarter]} ${p.data.period_year}` : '' },
    { colId: 'type', headerName: 'Тип', width: 130, sortable: false,
      valueGetter: (p) => p.data ? REPORT_TYPE_LABELS[p.data.report_type] ?? p.data.report_type : '' },
    { field: 'document_number', headerName: '№', width: 170 },
    { field: 'organization_id', headerName: 'Організація', width: 220,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '' },
    { colId: 'outcome', headerName: 'Підсумок', width: 200, sortable: false,
      valueGetter: (p) => p.data ? reportOutcome(p.data) : '' },
    { colId: 'submission', headerName: 'Подання', width: 140, sortable: false,
      cellRenderer: (p: { data?: DisabilityContributionReport }) => {
        if (!p.data) return null;
        const st = subByDoc.get(p.data.id);
        const [txt, color] = (st && SUB_STATUS[st]) || ['—', 'var(--muted)'];
        return <span style={{ color, fontWeight: st === 'ACCEPTED' ? 600 : 400 }}>{txt}</span>;
      } },
    { field: 'created_at', headerName: 'Створено', width: 160,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '' },
  ], [subByDoc, orgName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Звіт про внесок (особи з інвалідністю)<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/taxation/disability-contribution-reports/new')}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg ? `Немає прав у жодній з ${totalAccessibleOrgs} організацій` : undefined}>
          + Створити за квартал
        </button>
      </div>
      <DocListFiltersRow>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 220, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgListQ.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={fQuarter} onChange={(e) => setFQuarter(e.target.value)} style={{ width: 150, flex: '0 0 auto' }}>
          <option value="">— квартал —</option>
          {[1, 2, 3, 4].map((q) => <option key={q} value={String(q)}>{QUARTER_LABELS[q]}</option>)}
        </select>
        <select value={fYear} onChange={(e) => setFYear(e.target.value)} style={{ width: 110, flex: '0 0 auto' }}>
          <option value="">— рік —</option>
          {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() + 1 - i).filter((y) => y >= 2026).map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>
      </DocListFiltersRow>
      <InfiniteDocGrid<DisabilityContributionReport>
        datasource={datasource}
        columnDefs={cols}
        storageKey="disability-contribution-reports"
        onRowDoubleClicked={(r) => navigate(`/taxation/disability-contribution-reports/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
